import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Area, AreaChart,
} from 'recharts';
import { TrendingUp, Package, ShoppingBag, Star, AlertCircle, ArrowUpRight, Loader2 } from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { vendorApi } from '@/services/api';
import { getVendorIdFromToken } from '@/services/auth';
import { StatCard, type StatTone } from '@/components/shared/StatCard';
import { formatNPR } from '@/utils/format';
import { Badge, Button, Card, CardContent, CardHeader, CardTitle, cn } from '@nepal-ecom/ui';

const STATUS_VARIANTS: Record<string, 'secondary' | 'success' | 'warning' | 'info' | 'destructive' | 'default'> = {
  pending: 'warning',
  payment_confirmed: 'info',
  processing: 'info',
  packed: 'info',
  shipped: 'default',
  out_for_delivery: 'default',
  delivered: 'success',
  cancelled: 'destructive',
};

interface VendorStats {
  totalRevenue?: number;
  totalOrders?: number;
  pendingOrders?: number;
  totalProducts?: number;
  averageRating?: number;
}

interface Analytics {
  revenueByDay?: Array<{ date: string; revenue: number }>;
}

interface RecentOrder {
  id: string;
  orderNumber: string;
  status: string;
  // New `/orders/vendor` shape returns `vendorSubtotal` (vendor-scoped slice
  // of the order); `total` is kept as a fallback for any legacy callers.
  total?: string | number;
  vendorSubtotal?: string | number;
  // New shape uses `shippingAddress`; older shape used `deliveryAddress`.
  shippingAddress?: { fullName?: string };
  deliveryAddress?: { fullName?: string };
  createdAt: string;
}

interface ProductLite {
  id: string;
  name: string;
  sku?: string | null;
  stock: number;
}

const num = (v: string | number | null | undefined) => (v == null ? 0 : typeof v === 'string' ? Number(v) : v);

function RevenueChart({ data }: { data: Array<{ date: string; revenue: number }> }) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
        <div>
          <CardTitle>Revenue</CardTitle>
          <p className="text-xs text-slate-500 mt-0.5">{data.length > 0 ? `Last ${data.length} days` : 'No revenue data yet'}</p>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        {data.length === 0 ? (
          <div className="h-[240px] flex items-center justify-center text-sm text-slate-400">
            Revenue will appear once you start receiving orders.
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={240}>
            <AreaChart data={data} margin={{ top: 8, right: 10, left: 0, bottom: 5 }}>
              <defs>
                <linearGradient id="revGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#D95100" stopOpacity={0.2} />
                  <stop offset="100%" stopColor="#D95100" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
              <XAxis dataKey="date" tickFormatter={(d) => format(new Date(d), 'dd MMM')}
                tick={{ fontSize: 11, fill: '#94A3B8' }} axisLine={false} tickLine={false} />
              <YAxis tickFormatter={(v) => `रु.${(v / 1000).toFixed(0)}k`}
                tick={{ fontSize: 11, fill: '#94A3B8' }} axisLine={false} tickLine={false} width={60} />
              <Tooltip formatter={(v: number) => [formatNPR(v), 'Revenue']}
                labelFormatter={(l) => format(new Date(l), 'dd MMM yyyy')}
                contentStyle={{ borderRadius: 10, border: '1px solid #E2E8F0', fontSize: 12 }} />
              <Area type="monotone" dataKey="revenue" stroke="#D95100" strokeWidth={2}
                fill="url(#revGradient)" activeDot={{ r: 4, fill: '#D95100' }} />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  );
}

function RecentOrders({ orders }: { orders: RecentOrder[] }) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
        <CardTitle>Recent orders</CardTitle>
        <Button variant="ghost" size="sm" asChild>
          <Link to="/orders" className="text-xs">View all <ArrowUpRight className="h-3 w-3" /></Link>
        </Button>
      </CardHeader>
      <CardContent className="pt-0 pb-0">
        {orders.length === 0 ? (
          <div className="py-10 text-center">
            <ShoppingBag className="h-5 w-5 text-slate-400 mx-auto mb-2" />
            <p className="text-sm font-medium text-slate-700">No orders yet</p>
            <p className="text-xs text-slate-500 mt-0.5">New orders will appear here</p>
          </div>
        ) : (
          <ul className="divide-y divide-slate-100 -mx-5">
            {orders.slice(0, 6).map((o) => (
              <li key={o.id} className="flex items-center gap-4 px-5 py-3.5 hover:bg-slate-50 transition-colors">
                <div className="flex-1 min-w-0">
                  <Link to={`/orders/${o.id}`} className="text-sm font-semibold text-slate-900 tabular-nums hover:text-emerald-700">
                    #{o.orderNumber}
                  </Link>
                  <p className="text-xs text-slate-500 truncate">
                    {o.shippingAddress?.fullName ?? o.deliveryAddress?.fullName ?? '—'}
                  </p>
                </div>
                <Badge variant={STATUS_VARIANTS[o.status] ?? 'secondary'} className="capitalize">
                  {o.status.replace(/_/g, ' ')}
                </Badge>
                <span className="text-sm font-bold text-slate-900 tabular-nums flex-shrink-0 min-w-[80px] text-right">
                  {formatNPR(num(o.vendorSubtotal ?? o.total))}
                </span>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

function LowStockAlerts({ products }: { products: ProductLite[] }) {
  if (products.length === 0) {
    return (
      <Card className="bg-gradient-to-br from-emerald-50 to-white border-emerald-100">
        <CardContent className="p-5">
          <div className="flex items-start gap-3">
            <div className="h-10 w-10 rounded-xl bg-emerald-100 flex items-center justify-center flex-shrink-0">
              <Package className="h-5 w-5 text-emerald-600" />
            </div>
            <div>
              <p className="text-sm font-semibold text-emerald-900">Inventory healthy</p>
              <p className="text-xs text-emerald-700 mt-0.5">No low-stock alerts right now.</p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }
  return (
    <Card className="bg-gradient-to-br from-amber-50 to-white border-amber-200">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-amber-900">
          <AlertCircle className="h-4 w-4 text-amber-600" /> Low stock <Badge variant="warning">{products.length}</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0 space-y-2.5">
        {products.slice(0, 5).map((p) => {
          const pct = Math.min(100, Math.round((p.stock / 10) * 100));
          const barColor = pct < 25 ? 'bg-red-500' : pct < 60 ? 'bg-amber-500' : 'bg-emerald-500';
          return (
            <div key={p.id} className="space-y-1">
              <div className="flex items-center justify-between gap-2 text-sm">
                <Link to={`/products/${p.id}`} className="font-medium text-slate-900 truncate max-w-[180px] hover:underline">{p.name}</Link>
                <span className="font-bold text-amber-700 tabular-nums flex-shrink-0">{p.stock} left</span>
              </div>
              <div className="h-1.5 bg-amber-100 rounded-full overflow-hidden">
                <div className={cn('h-full rounded-full transition-all', barColor)} style={{ width: `${pct}%` }} />
              </div>
            </div>
          );
        })}
        <Link to="/products" className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-amber-800 hover:underline">
          Manage inventory <ArrowUpRight className="h-3 w-3" />
        </Link>
      </CardContent>
    </Card>
  );
}

export function Dashboard() {
  const [stats, setStats] = useState<VendorStats>({});
  const [revenueChart, setRevenueChart] = useState<Array<{ date: string; revenue: number }>>([]);
  const [recentOrders, setRecentOrders] = useState<RecentOrder[]>([]);
  const [lowStock, setLowStock] = useState<ProductLite[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const vendorId = getVendorIdFromToken();
    if (!vendorId) { setLoading(false); return; }
    (async () => {
      try {
        const [statsRes, analyticsRes, ordersRes, productsRes] = await Promise.allSettled([
          vendorApi.getVendorStats(vendorId),
          vendorApi.getAnalytics(vendorId),
          vendorApi.getOrders({ limit: 10 }),
          vendorApi.getProducts({ vendorId, limit: 100, status: 'all' }),
        ]);
        if (statsRes.status === 'fulfilled') {
          setStats(statsRes.value.data?.data ?? {});
        }
        if (analyticsRes.status === 'fulfilled') {
          const a = (analyticsRes.value.data?.data ?? {}) as Analytics;
          setRevenueChart(a.revenueByDay ?? []);
        }
        if (ordersRes.status === 'fulfilled') {
          const p = ordersRes.value.data?.data;
          setRecentOrders(Array.isArray(p) ? p : p?.data ?? []);
        }
        if (productsRes.status === 'fulfilled') {
          const p = productsRes.value.data?.data;
          const list = (Array.isArray(p) ? p : p?.data ?? []) as ProductLite[];
          setLowStock(list.filter((x) => x.stock > 0 && x.stock < 10));
        }
      } catch (e) {
        toast.error('Could not load dashboard', { description: (e as Error).message });
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const statCards: Array<{ icon: typeof TrendingUp; title: string; value: string; tone: StatTone }> = [
    { icon: TrendingUp, title: 'Lifetime revenue',   value: loading ? '—' : formatNPR(stats.totalRevenue ?? 0), tone: 'emerald' },
    { icon: ShoppingBag, title: 'Pending orders',    value: loading ? '—' : String(stats.pendingOrders ?? 0), tone: 'amber' },
    { icon: Package,    title: 'Total products',     value: loading ? '—' : String(stats.totalProducts ?? 0), tone: 'primary' },
    { icon: Star,       title: 'Average rating',     value: loading ? '—' : (stats.averageRating ?? 0).toFixed(1), tone: 'secondary' },
  ];

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Dashboard</h1>
          <p className="text-sm text-slate-500 mt-1">Welcome back — here&apos;s what&apos;s happening with your store.</p>
        </div>
        <Button asChild>
          <Link to="/products/new"><Package className="h-4 w-4" /> Add product</Link>
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        {statCards.map((c) => (
          <StatCard key={c.title} icon={c.icon} title={c.title} value={c.value} trend={0} tone={c.tone} />
        ))}
      </div>

      {loading ? (
        <Card><CardContent className="py-12 text-center"><Loader2 className="h-6 w-6 mx-auto animate-spin text-slate-400" /></CardContent></Card>
      ) : (
        <>
          <RevenueChart data={revenueChart} />
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
            <div className="xl:col-span-2"><RecentOrders orders={recentOrders} /></div>
            <LowStockAlerts products={lowStock} />
          </div>
        </>
      )}
    </div>
  );
}
